/**
 * Clears authenticated-user persisted data and notifies in-memory stores.
 * Preserves device/app settings (theme, device id, FCM token, remember-me email).
 */
import { STORAGE_KEYS, USER_SESSION_STORAGE_KEYS } from '../../constants';
import { storage } from '../../platform/storage';
import { eventBus } from '../../platform/eventBus';

/** Fired after user-session storage is wiped so Context providers can reset. */
export const USER_SESSION_CLEARED_EVENT = 'AUTH_USER_SESSION_CLEARED' as const;

export interface ClearUserSessionOptions {
  /**
   * When false, leave access/refresh tokens in place (e.g. mid-onboarding has
   * tokens but is not fully authenticated). Default true.
   */
  includeAuthTokens?: boolean;
  /**
   * When false, leave USER_GUID / USER_ID (rare). Default true.
   */
  includeUserIdentity?: boolean;
}

const AUTH_TOKEN_KEYS: readonly string[] = [
  STORAGE_KEYS.AUTH_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
  STORAGE_KEYS.TOKEN_EXPIRES_AT,
];

const IDENTITY_KEYS: readonly string[] = [
  STORAGE_KEYS.USER_ID,
  STORAGE_KEYS.USER_GUID,
];

/**
 * Removes every user-specific storage key and emits USER_SESSION_CLEARED_EVENT
 * so Feed / Profile / Messages / Groups / Survey / Notifications reset.
 */
export const clearUserSessionData = async (
  options: ClearUserSessionOptions = {},
): Promise<void> => {
  const includeAuthTokens = options.includeAuthTokens !== false;
  const includeUserIdentity = options.includeUserIdentity !== false;

  const keysToRemove = USER_SESSION_STORAGE_KEYS.filter(key => {
    if (!includeAuthTokens && AUTH_TOKEN_KEYS.includes(key)) {
      return false;
    }
    if (!includeUserIdentity && IDENTITY_KEYS.includes(key)) {
      return false;
    }
    return true;
  });

  await Promise.all(keysToRemove.map(key => storage.removeItem(key)));
  eventBus.emit(USER_SESSION_CLEARED_EVENT);
};

/**
 * For Login / SignUp entry: wipe leftover user caches only when there is no
 * active session (and no mid-onboarding token). Idempotent.
 */
export const clearStaleUserSessionIfUnauthenticated = async (): Promise<void> => {
  const [accessToken, refreshToken] = await Promise.all([
    storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN),
    storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN),
  ]);

  if (accessToken || refreshToken) {
    return;
  }

  await clearUserSessionData({ includeAuthTokens: true });
};
